import { CalendarDays, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { SendOfferModal } from './SendOfferModal';
import { RequestModal } from './RequestModal';

const formatRoute = (trip, t) => {
  const originCity = trip.originCity || trip.originDetails?.city;
  const originCountry = trip.originCountry || trip.originDetails?.country;
  const destinationCity = trip.destinationCity || trip.destinationDetails?.city;
  const destinationCountry =
    trip.destinationCountry || trip.destinationDetails?.country;

  return `${originCity || t('home.from')}, ${originCountry || ''} → ${
    destinationCity || t('home.to')
  }, ${destinationCountry || ''}`;
};

const formatDeparture = (trip, t) => {
  if (!trip.departureDate) return t('tripCard.scheduleTbd');
  if (trip.departureTime) {
    return `${trip.departureDate} • ${trip.departureTime}`;
  }
  return trip.departureDate;
};

const formatCapacity = trip => {
  if (!trip.availableSpace) return '—';
  return `${trip.availableSpace} ${trip.spaceUnit || 'kg'}`;
};

const formatPrice = (trip, t) => {
  const currency = trip.currency || 'USD';
  if (trip.pricingMode === 'perKg' && trip.pricePerKg) {
    return `${currency} ${trip.pricePerKg}/${trip.spaceUnit || 'kg'}`;
  }
  if (trip.pricingMode === 'perUnit' && trip.pricePerUnit) {
    return `${currency} ${trip.pricePerUnit}/${trip.spaceUnit || 'unit'}`;
  }
  if (trip.pricingMode === 'flat' && trip.flatPrice) {
    return `${currency} ${trip.flatPrice} flat`;
  }
  if (trip.flatPrice) {
    return `${currency} ${trip.flatPrice}`;
  }
  return t('tripCard.contactForPrice');
};

export function TripResultCard({
  trip,
  primaryActionLabel = 'Request',
  secondaryActionLabel = 'View details',
  disableActions = false,
  disableReason = '',
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [ownerProfile, setOwnerProfile] = useState(null);

  // Determine which modal to use based on trip type
  const tripType = trip.tripType || 'shipping';
  const isShippingTrip = tripType === 'shipping';

  // Get owner ID from trip
  const ownerId = useMemo(
    () =>
      trip?.createdBy || trip?.ownerId || trip?.createdByUid || trip?.userId,
    [trip],
  );

  // Fetch owner profile for rating
  useEffect(() => {
    const fetchOwnerProfile = async () => {
      if (!ownerId) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', ownerId));
        if (userDoc.exists()) {
          setOwnerProfile(userDoc.data());
        }
      } catch (error) {
        console.error('Error fetching owner profile:', error);
      }
    };

    fetchOwnerProfile();
  }, [ownerId]);

  // Calculate rating from profile
  const rating = useMemo(() => {
    const value =
      ownerProfile?.averageRating ??
      ownerProfile?.rating ??
      ownerProfile?.stats?.rating ??
      0;
    return value ? Number(value) : 0;
  }, [ownerProfile]);

  const reviewCount =
    ownerProfile?.totalReviews ??
    ownerProfile?.reviewsCount ??
    ownerProfile?.stats?.reviewsCount ??
    0;

  const initials =
    trip.createdByName?.slice(0, 1).toUpperCase() ||
    trip.createdByEmail?.slice(0, 1).toUpperCase() ||
    'F';

  return (
    <article className="rounded-3xl bg-white dark:bg-slate-900 p-6 shadow-lg">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black dark:bg-slate-700 text-lg font-semibold text-yellow-400">
          {initials}
        </div>
        <div>
          <p className="text-lg font-semibold text-gray-900 dark:text-white">
            {trip.createdByName || t('tripCard.flexyUser')}
          </p>
          <div className="flex items-center gap-1">
            <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
            <span className="text-sm font-semibold text-yellow-500">
              {rating > 0 ? rating.toFixed(1) : '—'}
            </span>
            {reviewCount > 0 && (
              <span className="text-xs text-gray-500 dark:text-slate-400">
                ({reviewCount})
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-2 text-sm text-gray-700 dark:text-slate-300">
        <p className="font-medium text-gray-900 dark:text-white">
          {formatRoute(trip, t)}
        </p>
        <p className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400">
          <CalendarDays className="h-4 w-4 text-gray-400 dark:text-slate-500" />
          {formatDeparture(trip, t)}
        </p>
        <div className="flex flex-wrap gap-6 text-sm">
          <span>
            {t('tripCard.capacityLeft')}{' '}
            <strong className="text-gray-900 dark:text-white">
              {formatCapacity(trip)}
            </strong>
          </span>
          <span>
            {t('tripCard.price')}{' '}
            <strong className="text-gray-900 dark:text-white">
              {formatPrice(trip, t)}
            </strong>
          </span>
        </div>
        {trip.materialType ? (
          <p>
            {t('tripCard.material')}{' '}
            <strong className="dark:text-white">{trip.materialType}</strong>
          </p>
        ) : null}
        {/* Delivery method removed for carrying trips (I'm a Traveler page) */}
        {isShippingTrip && trip.deliveryMethod ? (
          <p>
            {t('tripCard.deliveryMethod')}{' '}
            <strong className="dark:text-white">{trip.deliveryMethod}</strong>
          </p>
        ) : null}
      </div>

      {disableActions && disableReason ? (
        <p className="mt-6 text-sm font-medium text-red-600 dark:text-red-400">
          {disableReason}
        </p>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-4">
        <button
          type="button"
          disabled={disableActions}
          className={`flex-1 rounded-2xl border border-gray-300 dark:border-slate-700 px-4 py-3 text-center text-sm font-semibold transition ${
            disableActions
              ? 'cursor-not-allowed border-gray-200 dark:border-slate-800 text-gray-400 dark:text-slate-500'
              : 'text-gray-900 dark:text-white hover:border-gray-400 dark:hover:border-slate-600'
          }`}
          onClick={() => {
            if (!disableActions && trip.id) {
              navigate(`/trips/detail/${trip.id}`);
            }
          }}
        >
          {secondaryActionLabel}
        </button>
        <button
          type="button"
          disabled={disableActions}
          className={`flex-1 rounded-2xl px-4 py-3 text-center text-sm font-semibold transition ${
            disableActions
              ? 'cursor-not-allowed bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-slate-500'
              : 'bg-black dark:bg-slate-700 text-yellow-400 hover:bg-gray-900 dark:hover:bg-slate-800'
          }`}
          onClick={() => {
            if (!disableActions && trip.id) {
              if (isShippingTrip) {
                setShowRequestModal(true);
              } else {
                setShowOfferModal(true);
              }
            }
          }}
        >
          {disableActions && disableReason ? disableReason : primaryActionLabel}
        </button>
      </div>

      {/* Request Modal for shipping trips (sender requesting from traveler) */}
      {isShippingTrip && (
        <RequestModal
          visible={showRequestModal}
          onClose={() => setShowRequestModal(false)}
          trip={trip}
        />
      )}

      {/* Send Offer Modal for carrying trips (traveler offering to sender) */}
      {!isShippingTrip && (
        <SendOfferModal
          visible={showOfferModal}
          onClose={() => setShowOfferModal(false)}
          trip={trip}
        />
      )}
    </article>
  );
}
